// web/src/screens/OwnerDashboardScreen.jsx
import { useState, useEffect, useCallback } from 'react';
import { useNav, useAuth } from '../App';
import { apiClient } from '../api';

const METHOD_LABEL = {
  mpesa_stk:     { label: 'M-PESA STK',    color: 'var(--success)', icon: '💳' },
  mpesa_manual:  { label: 'M-PESA MANUAL', color: 'var(--warning)', icon: '📝' },
  cash:          { label: 'CASH',           color: 'var(--info)',   icon: '💵' },
  card:          { label: 'CARD',           color: '#a78bfa',       icon: '💳' },
  tab:           { label: 'TAB',            color: 'var(--danger)', icon: '📋' },
  complimentary: { label: 'COMP',           color: 'var(--text-muted)', icon: '🎁' },
};

const TABS = ['OVERVIEW', 'PAYMENTS', 'STAFF', 'STOCK', 'MENU', 'ROLES', 'PERIODS'];

export default function OwnerDashboardScreen() {
  const { goBack }    = useNav();
  const { user, permissions } = useAuth();
  const [tab, setTab] = useState('OVERVIEW');

  // Data state
  const [summary,       setSummary]       = useState(null);
  const [rolePerms,     setRolePerms]     = useState({});
  const [payments,      setPayments]      = useState([]);
  const [alerts,        setAlerts]        = useState([]);
  const [stockItems,    setStockItems]    = useState([]);
  const [periods,       setPeriods]       = useState([]);
  const [tables,        setTables]        = useState([]);
  const [menuData,      setMenuData]      = useState({ categories: [], items: [] });
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);

  // Per-tab error states
  const [paymentsError, setPaymentsError] = useState('');
  const [alertsError,   setAlertsError]   = useState('');
  const [stockError,    setStockError]    = useState('');
  const [periodsError,  setPeriodsError]  = useState('');
  const [rolesError,    setRolesError]    = useState('');
  const [menuError,     setMenuError]     = useState('');

  const loadAll = useCallback(async () => {
    try {
      const [summaryRes, tablesRes] = await Promise.all([
        apiClient.get('/dashboard/summary'),
        apiClient.get('/tables'),
      ]);
      setSummary(summaryRes.data);
      setTables(tablesRes.data);
      setError(null);
    } catch (err) {
      setError('Could not load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPayments = useCallback(async () => {
    setPaymentsError('');
    try {
      const { data } = await apiClient.get('/dashboard/payments');
      setPayments(data);
    } catch (err) {
      setPaymentsError(
        err.status === 403
          ? 'You do not have permission to view payments.'
          : 'Could not load payments. Please try again.'
      );
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    setAlertsError('');
    try {
      const { data } = await apiClient.get('/inventory/alerts');
      setAlerts(data);
    } catch (err) {
      setAlertsError(
        err.status === 403
          ? 'You do not have permission to view stock alerts.'
          : 'Could not load stock alerts. Please try again.'
      );
    }
  }, []);

  const loadStock = useCallback(async () => {
    setStockError('');
    try {
      const { data } = await apiClient.get('/inventory');
      setStockItems(data);
    } catch (err) {
      setStockError(
        err.status === 403
          ? 'You do not have permission to view inventory.'
          : 'Could not load inventory. Please try again.'
      );
    }
  }, []);

  const loadPeriods = useCallback(async () => {
    setPeriodsError('');
    try {
      const { data } = await apiClient.get('/workperiods/history');
      setPeriods(data);
    } catch (err) {
      setPeriodsError(
        err.status === 403
          ? 'You do not have permission to view work period reports.'
          : 'Could not load work period history. Please try again.'
      );
    }
  }, []);

  const handleRestock = async (itemId, qty) => {
    await apiClient.patch(`/inventory/${itemId}/restock`, { qty });
    await Promise.all([loadStock(), loadAlerts()]);
  };

  const loadRolePerms = useCallback(async () => {
    setRolesError('');
    try {
      const { data } = await apiClient.get('/settings/role-permissions');
      setRolePerms(data);
    } catch (err) {
      setRolesError(
        err.status === 403
          ? 'Only the venue owner can manage role permissions.'
          : 'Could not load permissions. Please try again.'
      );
    }
  }, []);

  const loadMenu = useCallback(async () => {
    setMenuError('');
    try {
      const [catRes, itemRes] = await Promise.all([
        apiClient.get('/menu-admin/categories'),
        apiClient.get('/menu-admin/items'),
      ]);
      setMenuData({
        categories: catRes.data.categories || [],
        items:      itemRes.data.items      || [],
      });
    } catch (err) {
      setMenuError(
        err.status === 403
          ? 'You do not have permission to manage the menu.'
          : 'Could not load menu data. Please try again.'
      );
    }
  }, []);

  useEffect(() => {
    loadAll();
    const interval = setInterval(loadAll, 30000);
    return () => clearInterval(interval);
  }, [loadAll]);

  useEffect(() => {
    if (tab === 'PAYMENTS') loadPayments();
    if (tab === 'STOCK')  { loadAlerts(); loadStock(); }
    if (tab === 'ROLES')    loadRolePerms();
    if (tab === 'MENU')     loadMenu();
    if (tab === 'PERIODS')  loadPeriods();
  }, [tab, loadPayments, loadAlerts, loadStock, loadRolePerms, loadMenu, loadPeriods]);

  const handleTogglePerm = async (role, permission, currentValue) => {
    try {
      await apiClient.patch(`/settings/role-permissions/${role}/${permission}`, { enabled: !currentValue });
      setRolePerms(prev => ({
        ...prev,
        [role]: { ...prev[role], [permission]: !currentValue },
      }));
    } catch (err) {
      alert(err.data?.error || 'Could not update permission');
    }
  };

  const handleVerify = async (paymentId) => {
    try {
      await apiClient.patch(`/dashboard/payments/${paymentId}/verify`);
      setPayments(prev => prev.map(p =>
        p.id === paymentId ? { ...p, verified_by_owner: true, dispute_note: null } : p
      ));
    } catch (_) {}
  };

  const handleDispute = async (paymentId) => {
    const note = prompt('Dispute reason (optional):') ?? 'Disputed by owner';
    try {
      await apiClient.patch(`/dashboard/payments/${paymentId}/dispute`, { note });
      setPayments(prev => prev.map(p =>
        p.id === paymentId ? { ...p, dispute_note: note, verified_by_owner: false } : p
      ));
    } catch (_) {}
  };

  if (loading) {
    return (
      <div style={s.centered}>
        <span style={{ color: 'var(--text-muted)', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2 }}>
          LOADING DASHBOARD...
        </span>
      </div>
    );
  }

  return (
    <div style={s.container}>

      {/* Header */}
      <div style={s.header}>
        <div>
          <span style={s.headerTitle}>DASHBOARD</span>
          <span style={s.headerSub}> · {user?.venue_name || 'ServeIQ'}</span>
        </div>
        <button style={s.backBtn} onClick={goBack}>← TABLES</button>
      </div>

      {/* Error banner */}
      {error && <div style={s.errorBanner}>{error}</div>}

      {/* Tabs */}
      <div style={s.tabs}>
        {TABS.map(t => (
          <button
            key={t}
            style={{ ...s.tabBtn, ...(tab === t ? s.tabActive : {}) }}
            onClick={() => setTab(t)}
          >
            {t}
            {t === 'PAYMENTS' && summary?.flagged_payments > 0 && (
              <span style={s.badge}>{summary.flagged_payments}</span>
            )}
            {t === 'STOCK' && alerts.length > 0 && (
              <span style={s.badge}>{alerts.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={s.content}>
        {tab === 'OVERVIEW' && (
          <>
            {permissions?.can_start_workperiod && <ReopenPeriodCard />}
            <OverviewTab summary={summary} tables={tables} />
          </>
        )}
        {tab === 'PAYMENTS' && <PaymentsTab payments={payments} onVerify={handleVerify} onDispute={handleDispute} onLoad={loadPayments} error={paymentsError} />}
        {tab === 'STAFF'    && <StaffTab perfData={summary?.staff_performance || []} />}
        {tab === 'STOCK'    && <StockTab alerts={alerts} alertsError={alertsError}
                                          items={stockItems} itemsError={stockError}
                                          onRestock={handleRestock} />}
        {tab === 'MENU'     && <MenuTab menuData={menuData} onReload={loadMenu} plan={user?.plan} error={menuError} />}
        {tab === 'ROLES'    && <RolesTab perms={rolePerms} onToggle={handleTogglePerm} onLoad={loadRolePerms} error={rolesError} />}
        {tab === 'PERIODS'  && <PeriodsTab periods={periods} error={periodsError} />}
      </div>
    </div>
  );
}

// ─── Reopen Period Card ─────────────────────────────────────────────────────────

function ReopenPeriodCard() {
  const [state, setState] = useState('loading'); // loading | none | closed | open
  const [lastClosed, setLastClosed] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: current } = await apiClient.get('/workperiods/current');
        if (current) { setState('open'); return; }
        const { data: closed } = await apiClient.get('/workperiods/last-closed');
        if (closed) { setLastClosed(closed); setState('closed'); } else { setState('none'); }
      } catch {
        setState('none');
      }
    })();
  }, []);

  const handleReopen = async () => {
    if (!lastClosed?.id) return;
    try {
      await apiClient.post(`/workperiods/${lastClosed.id}/reopen`, {});
      setState('open');
    } catch (err) {
      alert(err.data?.error || 'Failed to reopen work period');
    }
  };

  if (state !== 'closed') return null;

  return (
    <div style={{ ...s.section, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
        The last work period is closed — no waiter can open a table until one is started.
      </span>
      <button style={s.periodReopenBtn} onClick={handleReopen}>REOPEN LAST PERIOD</button>
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ summary, tables }) {
  if (!summary) return null;

  const activeTables = tables.filter(t => t.status !== 'free');

  return (
    <div>
      {/* Big revenue number */}
      <div style={s.revenueCard}>
        <div style={s.revenueLabel}>TODAY'S REVENUE</div>
        <div style={s.revenueAmount}>
          KES {summary.revenue_today.toLocaleString()}
        </div>
        <div style={s.revenueSub}>
          {summary.closed_tickets} orders closed · {summary.active_tickets} active · {summary.active_tables}/{summary.total_tables} tables occupied
        </div>
      </div>

      {/* Payment method breakdown */}
      {summary.payment_breakdown.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>PAYMENT BREAKDOWN</div>
          <div style={s.breakdownGrid}>
            {summary.payment_breakdown.map(row => {
              const m = METHOD_LABEL[row.method] || { label: row.method.toUpperCase(), color: 'var(--text-muted)', icon: '·' };
              return (
                <div key={row.method} style={{ ...s.breakdownCard, borderColor: m.color }}>
                  <div style={{ color: m.color, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
                    {m.icon} {m.label}
                  </div>
                  <div style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, marginTop: 4 }}>
                    KES {row.total.toLocaleString()}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{row.count} payments</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Active tables */}
      {activeTables.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>ACTIVE TABLES</div>
          {activeTables.map(t => (
            <div key={t.id} style={s.tableRow}>
              <div>
                <span style={s.tableName}>{t.name}</span>
                {t.waiter_name && <span style={s.tableWaiter}> · {t.waiter_name}</span>}
              </div>
              <div style={s.tableRight}>
                {t.ticket_total > 0 && (
                  <span style={s.tableAmount}>KES {Number(t.ticket_total).toLocaleString()}</span>
                )}
                <span style={{
                  ...s.statusPill,
                  ...(STATUS_BG[t.status]
                    ? { backgroundColor: STATUS_BG[t.status], color: '#fff' }
                    : { backgroundColor: 'var(--surface-2)', color: 'var(--text)' }),
                }}>
                  {t.status.toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTables.length === 0 && summary.active_tickets === 0 && (
        <div style={s.empty}>No active tables right now</div>
      )}
    </div>
  );
}

const STATUS_BG = {
  active:   'var(--table-active)',
  sent:     '#7c6200', // no existing token for this sub-state — stays literal, still a theme-invariant saturated fill
  bill:     'var(--table-bill)',
  reserved: 'var(--table-reserved)',
};

// ─── Payments Tab ─────────────────────────────────────────────────────────────

function PaymentsTab({ payments, onVerify, onDispute, onLoad, error }) {
  useEffect(() => { onLoad(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div style={{ ...s.empty, color: 'var(--warning)' }}>⚠ {error}</div>;

  const stk    = payments.filter(p => p.method === 'mpesa_stk');
  const manual = payments.filter(p => p.method === 'mpesa_manual');
  const other  = payments.filter(p => !['mpesa_stk','mpesa_manual'].includes(p.method));
  const total  = payments.reduce((sum, p) => sum + p.amount, 0);

  if (payments.length === 0) {
    return <div style={s.empty}>No payments recorded today</div>;
  }

  return (
    <div>
      <div style={s.payTotals}>
        <span style={s.payTotalsLabel}>TODAY TOTAL</span>
        <span style={s.payTotalsAmount}>KES {total.toLocaleString()}</span>
        <span style={s.payTotalsCount}>{payments.length} payments</span>
      </div>
      {stk.length > 0 && (
        <PaymentSection title="M-PESA STK (AUTO-CONFIRMED)" color="var(--success)" payments={stk} showActions={false} />
      )}
      {manual.length > 0 && (
        <PaymentSection title="M-PESA MANUAL (SPOT-CHECK REQUIRED)" color="var(--warning)" payments={manual} showActions={true} onVerify={onVerify} onDispute={onDispute} />
      )}
      {other.length > 0 && (
        <PaymentSection title="CASH / OTHER" color="var(--info)" payments={other} showActions={false} />
      )}
    </div>
  );
}

function PaymentSection({ title, color, payments, showActions, onVerify, onDispute }) {
  const sectionTotal = payments.reduce((sum, p) => sum + p.amount, 0);
  return (
    <div style={s.section}>
      <div style={{ ...s.sectionTitle, color }}>
        {title}
        <span style={{ color: 'var(--text)', marginLeft: 8 }}>KES {sectionTotal.toLocaleString()}</span>
      </div>
      {payments.map(p => (
        <div key={p.id} style={{
          ...s.paymentRow,
          borderLeftColor: p.dispute_note ? 'var(--danger)' : p.verified_by_owner ? 'var(--success)' : color,
        }}>
          <div style={s.paymentRowTop}>
            <span style={s.paymentTable}>{p.table_name}</span>
            <span style={s.paymentAmount}>KES {p.amount.toLocaleString()}</span>
          </div>
          <div style={s.paymentRowSub}>
            <span style={{ color: 'var(--text-muted)' }}>{p.waiter_name}</span>
            {p.mpesa_code
              ? <span style={{ color: 'var(--text-muted)' }}> · {p.mpesa_code}</span>
              : showActions && <span style={{ color: 'var(--warning)' }}> · NO CODE ⚠</span>
            }
            <span style={{ color: 'var(--text-dim)', marginLeft: 'auto' }}>
              {new Date(p.recorded_at).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          {p.dispute_note && <div style={s.disputeNote}>⚠ {p.dispute_note}</div>}
          {showActions && !p.verified_by_owner && !p.dispute_note && (
            <div style={s.paymentActions}>
              <button style={s.verifyBtn} onClick={() => onVerify(p.id)}>✓ VERIFY</button>
              <button style={s.disputeBtn} onClick={() => onDispute(p.id)}>⚠ DISPUTE</button>
            </div>
          )}
          {p.verified_by_owner && (
            <div style={{ color: 'var(--success)', fontSize: 11, marginTop: 4, fontWeight: 700 }}>✓ VERIFIED</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Staff Tab ────────────────────────────────────────────────────────────────

const EMPTY_STAFF_FORM = { name: '', phone: '', role: 'waiter', pin: '' };

function StaffTab({ perfData }) {
  const [staffList,   setStaffList]   = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form,        setForm]        = useState(EMPTY_STAFF_FORM);
  const [saving,      setSaving]      = useState(false);
  const [formError,   setFormError]   = useState('');
  const [loadingList, setLoadingList] = useState(true);

  const loadStaff = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/staff');
      setStaffList(data.staff || data || []);
    } catch (_) {}
    finally { setLoadingList(false); }
  }, []);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  async function handleAddStaff() {
    setFormError('');
    if (!form.name.trim())                   return setFormError('Name is required');
    if (!form.phone.trim())                  return setFormError('Phone is required');
    if (!form.pin || form.pin.length !== 4)  return setFormError('PIN must be exactly 4 digits');
    setSaving(true);
    try {
      await apiClient.post('/staff', {
        name:  form.name.trim(),
        phone: form.phone.trim(),
        role:  form.role,
        pin:   form.pin,
      });
      setForm(EMPTY_STAFF_FORM);
      setShowAddForm(false);
      loadStaff();
    } catch (err) {
      setFormError(err.data?.error || 'Could not add staff member');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate(id, name) {
    if (!window.confirm(`Remove ${name} from staff? They will no longer be able to log in.`)) return;
    try {
      await apiClient.patch(`/staff/${id}/deactivate`);
      loadStaff();
    } catch (_) {}
  }

  const totalRevenue = perfData.reduce((sum, s) => sum + s.revenue, 0);

  return (
    <div>
      {/* Add staff button */}
      <div style={m.actionBar}>
        <button
          style={m.addBtn}
          onClick={() => { setShowAddForm(v => !v); setFormError(''); setForm(EMPTY_STAFF_FORM); }}
        >
          {showAddForm ? '✕ CANCEL' : '+ ADD STAFF MEMBER'}
        </button>
      </div>

      {/* Add staff form */}
      {showAddForm && (
        <div style={m.formCard}>
          <div style={m.formTitle}>NEW STAFF MEMBER</div>
          {formError && <div style={m.formError}>{formError}</div>}
          <input
            style={m.input}
            placeholder="Full name"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
          <input
            style={m.input}
            placeholder="Phone (+254712345678)"
            value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          />
          <select
            style={m.select}
            value={form.role}
            onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
          >
            <option value="waiter">Waiter</option>
            <option value="cashier">Cashier</option>
            <option value="manager">Manager</option>
          </select>
          <input
            style={m.input}
            placeholder="4-digit PIN"
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={form.pin}
            onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '') }))}
          />
          <button style={m.saveBtn} onClick={handleAddStaff} disabled={saving}>
            {saving ? 'SAVING...' : 'ADD MEMBER'}
          </button>
        </div>
      )}

      {/* Staff roster */}
      <div style={s.section}>
        <div style={s.sectionTitle}>STAFF ROSTER</div>
        {loadingList ? (
          <div style={s.empty}>Loading...</div>
        ) : staffList.length === 0 ? (
          <div style={s.empty}>No staff added yet</div>
        ) : (
          staffList.map(member => (
            <div key={member.id} style={{ ...s.staffRow, alignItems: 'center' }}>
              <div style={s.staffInfo}>
                <div style={s.staffName}>{member.name}</div>
                <div style={s.staffRole}>{member.role.toUpperCase()} · {member.phone}</div>
              </div>
              <button
                style={{ ...m.actionBtn, borderColor: 'var(--danger)', color: 'var(--danger)' }}
                onClick={() => handleDeactivate(member.id, member.name)}
              >
                REMOVE
              </button>
            </div>
          ))
        )}
      </div>

      {/* Today's performance */}
      {perfData.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>PERFORMANCE TODAY</div>
          {perfData.map((member, i) => {
            const pct = totalRevenue > 0 ? (member.revenue / totalRevenue) * 100 : 0;
            return (
              <div key={member.id} style={s.staffRow}>
                <div style={s.staffRank}>#{i + 1}</div>
                <div style={s.staffInfo}>
                  <div style={s.staffName}>{member.name}</div>
                  <div style={s.staffRole}>{member.role.toUpperCase()}</div>
                  <div style={s.barTrack}>
                    <div style={{ ...s.barFill, width: `${pct}%` }} />
                  </div>
                </div>
                <div style={s.staffStats}>
                  <div style={s.staffRevenue}>KES {member.revenue.toLocaleString()}</div>
                  <div style={s.staffOrders}>{member.orders_closed} orders</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Stock Tab ────────────────────────────────────────────────────────────────

function formatStockQty(item) {
  if (item.stock_unit === 'ml') {
    const ml = Number(item.stock_qty);
    return ml >= 1000 ? `${(ml / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} L` : `${ml.toLocaleString()} ml`;
  }
  return item.stock_qty.toLocaleString();
}

function StockRestockRow({ item, onRestock }) {
  const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false);
  const isOut = item.stock_qty <= 0;
  const isLow = item.stock_qty <= item.min_stock;

  const submit = async (amount) => {
    if (!amount || amount <= 0) return;
    setBusy(true);
    try {
      await onRestock(item.id, amount);
      setQty('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ ...s.stockRow, borderLeftColor: isOut ? 'var(--danger)' : isLow ? 'var(--warning)' : 'var(--border)' }}>
      <div>
        <div style={s.stockName}>{item.name}</div>
        <div style={s.stockCategory}>{item.category}</div>
      </div>
      <div style={{ textAlign: 'right', marginRight: 12 }}>
        <div style={{ color: isOut ? 'var(--danger)' : isLow ? 'var(--warning)' : 'var(--text)', fontWeight: 700, fontSize: 16 }}>
          {isOut ? 'OUT' : formatStockQty(item)}
        </div>
        <div style={{ color: 'var(--text-dim)', fontSize: 11 }}>min {item.min_stock}</div>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          type="number"
          inputMode="decimal"
          placeholder={item.stock_unit === 'ml' ? 'ml' : 'qty'}
          value={qty}
          onChange={e => setQty(e.target.value)}
          style={s.restockInput}
          disabled={busy}
        />
        <button style={s.restockBtn} disabled={busy} onClick={() => submit(parseFloat(qty))}>
          + Add
        </button>
        {item.stock_unit === 'ml' && item.unit_volume_ml > 0 && (
          <button style={s.restockBtn} disabled={busy} onClick={() => submit(Number(item.unit_volume_ml))}>
            +1 unit ({Number(item.unit_volume_ml).toLocaleString()}ml)
          </button>
        )}
      </div>
    </div>
  );
}

function StockTab({ alerts, alertsError, items, itemsError, onRestock }) {
  const [searchText, setSearchText] = useState('');
  const filteredItems = searchText.trim()
    ? items.filter(i => i.name.toLowerCase().includes(searchText.trim().toLowerCase()))
    : items;

  return (
    <div>
      {alertsError ? (
        <div style={{ ...s.empty, color: 'var(--warning)' }}>⚠ {alertsError}</div>
      ) : alerts.length > 0 && (
        <div style={s.section}>
          <div style={{ ...s.sectionTitle, color: 'var(--warning)' }}>
            LOW STOCK ALERTS ({alerts.length})
          </div>
          {alerts.map(item => {
            const isOut = item.stock_qty <= 0;
            return (
              <div key={item.id} style={{ ...s.stockRow, borderLeftColor: isOut ? 'var(--danger)' : 'var(--warning)' }}>
                <div>
                  <div style={s.stockName}>{item.name}</div>
                  <div style={s.stockCategory}>{item.category}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: isOut ? 'var(--danger)' : 'var(--warning)', fontWeight: 700, fontSize: 16 }}>
                    {isOut ? 'OUT' : formatStockQty(item)}
                  </div>
                  <div style={{ color: 'var(--text-dim)', fontSize: 11 }}>min {item.min_stock}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={s.section}>
        <div style={s.sectionTitle}>ALL TRACKED STOCK ({items.length})</div>
        {!itemsError && items.length > 0 && (
          <input
            type="text"
            placeholder="🔍 Search stock..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={s.stockSearchInput}
          />
        )}
        {itemsError ? (
          <div style={{ ...s.empty, color: 'var(--warning)' }}>⚠ {itemsError}</div>
        ) : items.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
            No stock-tracked items yet
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={s.empty}>No items match "{searchText.trim()}"</div>
        ) : (
          filteredItems.map(item => <StockRestockRow key={item.id} item={item} onRestock={onRestock} />)
        )}
      </div>
    </div>
  );
}

// ─── Menu Tab ─────────────────────────────────────────────────────────────────

const EMPTY_ITEM_FORM = { name: '', price: '', description: '', category_id: '', sort_order: 0 };
const EMPTY_CAT_FORM  = { name: '', sort_order: 0 };

function PlanLockCard({ title, description, requiredPlan }) {
  return (
    <div style={m.lockCard}>
      <div style={m.lockIcon}>🔒</div>
      <div style={m.lockTitle}>{title}</div>
      <div style={m.lockDesc}>{description}</div>
      <div style={m.lockPill}>
        UPGRADE TO {requiredPlan.toUpperCase()} — KES {requiredPlan === 'biz' ? '1,999' : '3,999'}/mo
      </div>
    </div>
  );
}

function MenuTab({ menuData, onReload, plan, error }) {
  const { categories, items } = menuData;

  const [selectedCat,  setSelectedCat]  = useState('ALL');
  const [showAddItem,  setShowAddItem]  = useState(false);
  const [showAddCat,   setShowAddCat]   = useState(false);
  const [editingItem,  setEditingItem]  = useState(null);
  const [itemForm,     setItemForm]     = useState(EMPTY_ITEM_FORM);
  const [catForm,      setCatForm]      = useState(EMPTY_CAT_FORM);
  const [saving,       setSaving]       = useState(false);
  const [formError,    setFormError]    = useState('');

  if (error) return <div style={{ ...s.empty, color: 'var(--warning)' }}>⚠ {error}</div>;

  const canBiz   = plan === 'biz'  || plan === 'club';
  const canClub  = plan === 'club';

  // Filter items by selected category
  const visibleItems = selectedCat === 'ALL'
    ? items
    : items.filter(i => i.category_id === selectedCat);

  // ── Add Item ──────────────────────────────────────────────────────────────

  async function handleAddItem () {
    setFormError('');
    if (!itemForm.name.trim())    return setFormError('Item name is required');
    if (!itemForm.price)          return setFormError('Price is required');
    if (!itemForm.category_id)    return setFormError('Select a category');

    setSaving(true);
    try {
      await apiClient.post('/menu-admin/items', {
        name:        itemForm.name.trim(),
        price:       parseFloat(itemForm.price),
        description: itemForm.description.trim() || undefined,
        category_id: itemForm.category_id,
        sort_order:  parseInt(itemForm.sort_order) || 0,
      });
      setItemForm(EMPTY_ITEM_FORM);
      setShowAddItem(false);
      onReload();
    } catch (err) {
      setFormError(err.data?.error || 'Could not add item');
    } finally {
      setSaving(false);
    }
  }

  // ── Edit Item ─────────────────────────────────────────────────────────────

  function openEdit (item) {
    setEditingItem(item);
    setItemForm({
      name:        item.name,
      price:       item.price,
      description: item.description || '',
      category_id: item.category_id || '',
      sort_order:  item.sort_order  || 0,
    });
    setFormError('');
    setShowAddItem(false);
    setShowAddCat(false);
  }

  async function handleEditItem () {
    setFormError('');
    if (!itemForm.name.trim()) return setFormError('Item name is required');
    if (!itemForm.price)       return setFormError('Price is required');

    setSaving(true);
    try {
      await apiClient.patch(`/menu-admin/items/${editingItem.id}`, {
        name:        itemForm.name.trim(),
        price:       parseFloat(itemForm.price),
        description: itemForm.description.trim() || undefined,
        category_id: itemForm.category_id || undefined,
        sort_order:  parseInt(itemForm.sort_order) || 0,
      });
      setEditingItem(null);
      setItemForm(EMPTY_ITEM_FORM);
      onReload();
    } catch (err) {
      setFormError(err.data?.error || 'Could not update item');
    } finally {
      setSaving(false);
    }
  }

  // ── Toggle Item ───────────────────────────────────────────────────────────

  async function handleToggle (item) {
    try {
      await apiClient.patch(`/menu-admin/items/${item.id}/toggle`);
      onReload();
    } catch (_) {}
  }

  // ── Add Category ──────────────────────────────────────────────────────────

  async function handleAddCategory () {
    setFormError('');
    if (!catForm.name.trim()) return setFormError('Category name is required');

    setSaving(true);
    try {
      await apiClient.post('/menu-admin/categories', {
        name:       catForm.name.trim(),
        sort_order: parseInt(catForm.sort_order) || 0,
      });
      setCatForm(EMPTY_CAT_FORM);
      setShowAddCat(false);
      onReload();
    } catch (err) {
      setFormError(err.data?.error || 'Could not add category');
    } finally {
      setSaving(false);
    }
  }

  // ── Delete Item (soft) ────────────────────────────────────────────────────

  async function handleDeleteItem (item) {
    if (!window.confirm(`Remove "${item.name}" from the menu?`)) return;
    try {
      await apiClient.delete(`/menu-admin/items/${item.id}`);
      onReload();
    } catch (_) {}
  }

  const isEditing = !!editingItem;
  const showForm  = showAddItem || isEditing;

  return (
    <div>
      {/* ── Top action bar ─────────────────────────────────────────────── */}
      <div style={m.actionBar}>
        <button
          style={m.addBtn}
          onClick={() => { setShowAddItem(!showAddItem); setEditingItem(null); setItemForm(EMPTY_ITEM_FORM); setFormError(''); setShowAddCat(false); }}
        >
          {showAddItem ? '✕ CANCEL' : '+ ADD ITEM'}
        </button>
        <button
          style={{ ...m.addBtn, background: 'var(--surface-2)', color: 'var(--text-muted)' }}
          onClick={() => { setShowAddCat(!showAddCat); setFormError(''); setShowAddItem(false); setEditingItem(null); }}
        >
          {showAddCat ? '✕ CANCEL' : '+ CATEGORY'}
        </button>
      </div>

      {/* ── Add Category form ──────────────────────────────────────────── */}
      {showAddCat && (
        <div style={m.formCard}>
          <div style={m.formTitle}>NEW CATEGORY</div>
          {formError && <div style={m.formError}>{formError}</div>}
          <input
            style={m.input}
            placeholder="Category name (e.g. Beers, Spirits, Food)"
            value={catForm.name}
            onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))}
          />
          <input
            style={m.input}
            placeholder="Sort order (0 = first)"
            type="number"
            value={catForm.sort_order}
            onChange={e => setCatForm(p => ({ ...p, sort_order: e.target.value }))}
          />
          <button style={m.saveBtn} onClick={handleAddCategory} disabled={saving}>
            {saving ? 'SAVING...' : 'SAVE CATEGORY'}
          </button>
        </div>
      )}

      {/* ── Add / Edit Item form ───────────────────────────────────────── */}
      {showForm && (
        <div style={m.formCard}>
          <div style={m.formTitle}>{isEditing ? `EDIT: ${editingItem.name}` : 'NEW ITEM'}</div>
          {formError && <div style={m.formError}>{formError}</div>}
          <input
            style={m.input}
            placeholder="Item name"
            value={itemForm.name}
            onChange={e => setItemForm(p => ({ ...p, name: e.target.value }))}
          />
          <input
            style={m.input}
            placeholder="Price (KES)"
            type="number"
            value={itemForm.price}
            onChange={e => setItemForm(p => ({ ...p, price: e.target.value }))}
          />
          <input
            style={m.input}
            placeholder="Description (optional)"
            value={itemForm.description}
            onChange={e => setItemForm(p => ({ ...p, description: e.target.value }))}
          />
          <select
            style={m.select}
            value={itemForm.category_id}
            onChange={e => setItemForm(p => ({ ...p, category_id: e.target.value }))}
          >
            <option value="">Select category...</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={m.saveBtn}
              onClick={isEditing ? handleEditItem : handleAddItem}
              disabled={saving}
            >
              {saving ? 'SAVING...' : isEditing ? 'SAVE CHANGES' : 'ADD ITEM'}
            </button>
            {isEditing && (
              <button
                style={{ ...m.saveBtn, background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
                onClick={() => { setEditingItem(null); setItemForm(EMPTY_ITEM_FORM); }}
              >
                CANCEL
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Category filter pills ──────────────────────────────────────── */}
      <div style={m.catRow}>
        {[{ id: 'ALL', name: `ALL (${items.length})` }, ...categories].map(c => (
          <button
            key={c.id}
            style={{
              ...m.catPill,
              ...(selectedCat === c.id ? m.catPillActive : {}),
            }}
            onClick={() => setSelectedCat(c.id)}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* ── Items list ─────────────────────────────────────────────────── */}
      <div style={{ padding: '0 12px' }}>
        {visibleItems.length === 0 && (
          <div style={s.empty}>
            {categories.length === 0
              ? 'Add a category first, then add items'
              : 'No items in this category'}
          </div>
        )}

        {visibleItems.map(item => (
          <div
            key={item.id}
            style={{
              ...m.itemRow,
              opacity: item.active ? 1 : 0.45,
              borderLeftColor: item.active ? 'var(--orange)' : 'var(--border)',
            }}
          >
            {/* Left: name + meta */}
            <div style={{ flex: 1 }}>
              <div style={m.itemName}>{item.name}</div>
              <div style={m.itemMeta}>
                {item.category_name && (
                  <span style={m.catBadge}>{item.category_name}</span>
                )}
                {item.description && (
                  <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>{item.description}</span>
                )}
              </div>
              {/* Margin — Club only */}
              {canClub && item.margin_pct !== null && item.margin_pct !== undefined && (
                <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
                  Margin: <span style={{ color: item.margin_pct > 50 ? 'var(--success)' : 'var(--warning)' }}>
                    {item.margin_pct}%
                  </span>
                </div>
              )}
            </div>

            {/* Right: price + actions */}
            <div style={m.itemRight}>
              <div style={m.itemPrice}>KES {parseFloat(item.price).toLocaleString()}</div>
              <div style={m.itemActions}>
                {/* Toggle active */}
                <button
                  style={{
                    ...m.actionBtn,
                    color:       item.active ? 'var(--success)' : 'var(--text-dim)',
                    borderColor: item.active ? 'var(--success)' : 'var(--border)',
                  }}
                  onClick={() => handleToggle(item)}
                  title={item.active ? 'Disable item' : 'Enable item'}
                >
                  {item.active ? 'ON' : 'OFF'}
                </button>
                {/* Edit */}
                <button
                  style={{ ...m.actionBtn, color: 'var(--orange)', borderColor: 'var(--orange)' }}
                  onClick={() => openEdit(item)}
                >
                  EDIT
                </button>
                {/* Delete */}
                <button
                  style={{ ...m.actionBtn, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                  onClick={() => handleDeleteItem(item)}
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Biz+ features ──────────────────────────────────────────────── */}
      <div style={{ padding: '16px 12px 0' }}>
        <div style={s.sectionTitle}>BIZ+ FEATURES</div>
      </div>

      {canBiz ? (
        <div style={{ padding: '0 12px' }}>
          {/* Happy Hour — placeholder for future UI */}
          <div style={m.bizCard}>
            <div style={m.bizCardTitle}>⏰ HAPPY HOUR PRICING</div>
            <div style={m.bizCardDesc}>
              Set time-based prices for specific items. Coming soon — use the API directly for now.
            </div>
          </div>
          {/* Bulk Price — placeholder for future UI */}
          <div style={m.bizCard}>
            <div style={m.bizCardTitle}>💰 BULK PRICE UPDATE</div>
            <div style={m.bizCardDesc}>
              Update prices across all items or a category at once. Coming soon — use the API directly for now.
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '0 12px' }}>
          <PlanLockCard
            title="Happy Hour Pricing"
            description="Set time-based prices for items. Perfect for sundowners and late-night specials."
            requiredPlan="biz"
          />
          <PlanLockCard
            title="Bulk Price Update"
            description="Apply a percentage or flat change across all items or a whole category instantly."
            requiredPlan="biz"
          />
        </div>
      )}

      {/* ── Club features ───────────────────────────────────────────────── */}
      <div style={{ padding: '16px 12px 0' }}>
        <div style={s.sectionTitle}>CLUB FEATURES</div>
      </div>

      {canClub ? (
        <div style={{ padding: '0 12px 20px' }}>
          <div style={m.bizCard}>
            <div style={m.bizCardTitle}>📊 MARGIN TRACKING</div>
            <div style={m.bizCardDesc}>
              Set cost price on each item using the EDIT button above to see your margin percentage.
            </div>
          </div>
          <div style={m.bizCard}>
            <div style={m.bizCardTitle}>🤖 AI SALES INSIGHTS</div>
            <div style={m.bizCardDesc}>
              Best sellers, slow movers, and margin opportunities — powered by 30 days of your sales data.
              Set your Anthropic API key first, then tap below.
            </div>
            <AiInsightsBlock />
          </div>
        </div>
      ) : (
        <div style={{ padding: '0 12px 20px' }}>
          <PlanLockCard
            title="Margin / Cost Tracking"
            description="Set cost prices per item and see your profit margin on every product."
            requiredPlan="club"
          />
          <PlanLockCard
            title="AI Sales Insights"
            description="Claude analyses your last 30 days of sales and highlights your best sellers, slow movers, and margin opportunities."
            requiredPlan="club"
          />
        </div>
      )}
    </div>
  );
}

// ─── AI Insights Block (Club only, embedded inside MenuTab) ───────────────────

function AiInsightsBlock () {
  const [insights,  setInsights]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  async function load () {
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get('/ai/insights');
      setInsights(data.insights);
    } catch (err) {
      setError(err.data?.message || 'Could not load insights. Check that your Anthropic API key is configured.');
    } finally {
      setLoading(false);
    }
  }

  if (!insights && !loading) {
    return (
      <button style={m.saveBtn} onClick={load}>
        GENERATE INSIGHTS
      </button>
    );
  }

  if (loading) return <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>Analysing your sales data…</div>;
  if (error)   return <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>{error}</div>;

  return (
    <div style={{ marginTop: 12 }}>
      {/* Summary */}
      <div style={{ color: 'var(--text)', fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
        {insights.summary}
      </div>

      {/* Best sellers */}
      {insights.best_sellers?.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: 'var(--success)', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
            🏆 BEST SELLERS
          </div>
          {insights.best_sellers.map((item, i) => (
            <div key={i} style={m.insightRow}>
              <div style={m.insightName}>{item.name}</div>
              <div style={m.insightStat}>
                {item.qty_sold} sold · KES {Number(item.revenue).toLocaleString()}
              </div>
              <div style={m.insightTip}>{item.insight}</div>
            </div>
          ))}
        </div>
      )}

      {/* Slow movers */}
      {insights.slow_movers?.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: 'var(--warning)', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
            🐢 SLOW MOVERS
          </div>
          {insights.slow_movers.map((item, i) => (
            <div key={i} style={m.insightRow}>
              <div style={m.insightName}>{item.name}</div>
              <div style={m.insightStat}>{item.qty_sold} sold</div>
              <div style={m.insightTip}>{item.insight}</div>
            </div>
          ))}
        </div>
      )}

      {/* Margin opportunities */}
      {insights.margin_opportunities?.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
            💡 MARGIN OPPORTUNITIES
          </div>
          {insights.margin_opportunities.map((item, i) => (
            <div key={i} style={m.insightRow}>
              <div style={m.insightName}>{item.name}</div>
              <div style={m.insightStat}>Margin: {item.margin_pct}%</div>
              <div style={m.insightTip}>{item.insight}</div>
            </div>
          ))}
        </div>
      )}

      <button
        style={{ ...m.saveBtn, background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', marginTop: 8 }}
        onClick={load}
      >
        ↺ REFRESH INSIGHTS
      </button>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  container:     { backgroundColor: 'var(--bg)', height: '100dvh', display: 'flex', flexDirection: 'column',
                   fontFamily: "'Barlow Condensed', sans-serif" },
  centered:      { display: 'flex', alignItems: 'center', justifyContent: 'center',
                   minHeight: '100vh' },
  header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                   padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  headerTitle:   { color: 'var(--orange)', fontSize: 20, fontWeight: 700, letterSpacing: 2 },
  headerSub:     { color: 'var(--text-muted)', fontSize: 14 },
  backBtn:       { background: 'none', border: 'none', color: 'var(--orange)', fontSize: 13,
                   fontWeight: 700, cursor: 'pointer', letterSpacing: 1, fontFamily: 'inherit' },
  errorBanner:   { background: 'var(--surface-2)', color: 'var(--danger)', padding: '8px 16px',
                   fontSize: 13, borderBottom: '1px solid var(--border)', flexShrink: 0 },
  tabs:          { display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)',
                   overflowX: 'auto', flexShrink: 0 },
  tabBtn:        { flex: '0 0 auto', background: 'none', border: 'none', color: 'var(--text-muted)',
                   fontSize: 12, fontWeight: 700, padding: '12px 10px', cursor: 'pointer',
                   letterSpacing: 1, fontFamily: 'inherit', position: 'relative',
                   display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
  tabActive:     { color: 'var(--orange)', borderBottom: '2px solid var(--orange)' },
  badge:         { background: 'var(--danger)', color: '#fff', borderRadius: 10,
                   fontSize: 10, fontWeight: 700, padding: '1px 5px', minWidth: 16,
                   textAlign: 'center' },
  content:       { flex: 1, overflowY: 'auto', padding: '0 0 20px 0' },
  revenueCard:   { background: 'var(--surface)', margin: 12, borderRadius: 10,
                   padding: '20px 20px', borderLeft: '4px solid var(--orange)' },
  revenueLabel:  { color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, letterSpacing: 2 },
  revenueAmount: { color: 'var(--orange)', fontSize: 36, fontWeight: 700, letterSpacing: 1, marginTop: 4 },
  revenueSub:    { color: 'var(--text-muted)', fontSize: 12, marginTop: 6 },
  breakdownGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 },
  breakdownCard: { background: 'var(--surface)', borderRadius: 8, padding: '12px 14px',
                   borderLeft: '3px solid var(--border)' },
  section:       { padding: '12px 12px 0' },
  sectionTitle:  { color: 'var(--text-muted)', fontSize: 12, fontWeight: 700, letterSpacing: 2,
                   marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--surface)' },
  tableRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                   padding: '10px 14px', background: 'var(--surface)', borderRadius: 8, marginBottom: 6 },
  tableName:     { color: 'var(--text)', fontWeight: 700, fontSize: 15 },
  tableWaiter:   { color: 'var(--text-muted)', fontSize: 13 },
  tableRight:    { display: 'flex', alignItems: 'center', gap: 8 },
  tableAmount:   { color: 'var(--orange)', fontWeight: 700, fontSize: 14 },
  statusPill:    { borderRadius: 4, padding: '2px 7px', fontSize: 10,
                   fontWeight: 700, letterSpacing: 1 },
  payTotals:     { background: 'var(--surface)', padding: '12px 16px', display: 'flex',
                   alignItems: 'baseline', gap: 12, borderBottom: '1px solid var(--border)' },
  payTotalsLabel:  { color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, letterSpacing: 2 },
  payTotalsAmount: { color: 'var(--text)', fontSize: 22, fontWeight: 700 },
  payTotalsCount:  { color: 'var(--text-dim)', fontSize: 12 },
  paymentRow:    { background: 'var(--surface)', borderRadius: 8, padding: '10px 12px',
                   marginBottom: 6, borderLeft: '3px solid var(--border)' },
  paymentRowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  paymentTable:  { color: 'var(--text)', fontWeight: 700, fontSize: 15 },
  paymentAmount: { color: 'var(--orange)', fontWeight: 700, fontSize: 15 },
  paymentRowSub: { display: 'flex', alignItems: 'center', marginTop: 4, fontSize: 12 },
  disputeNote:   { color: 'var(--danger)', fontSize: 11, marginTop: 6, fontWeight: 700 },
  paymentActions:{ display: 'flex', gap: 8, marginTop: 8 },
  verifyBtn:     { background: 'none', border: '1px solid var(--success)', color: 'var(--success)',
                   borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700,
                   cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 1 },
  disputeBtn:    { background: 'none', border: '1px solid var(--danger)', color: 'var(--danger)',
                   borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700,
                   cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 1 },
  staffRow:      { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                   background: 'var(--surface)', borderRadius: 8, marginBottom: 6 },
  staffRank:     { color: 'var(--text-dim)', fontSize: 18, fontWeight: 700, width: 28, textAlign: 'center' },
  staffInfo:     { flex: 1 },
  staffName:     { color: 'var(--text)', fontSize: 16, fontWeight: 700 },
  staffRole:     { color: 'var(--text-muted)', fontSize: 11, letterSpacing: 1, marginBottom: 6 },
  barTrack:      { height: 4, background: 'var(--surface-2)', borderRadius: 2, overflow: 'hidden' },
  barFill:       { height: '100%', background: 'var(--orange)', borderRadius: 2, transition: 'width 0.5s ease' },
  staffStats:    { textAlign: 'right' },
  staffRevenue:  { color: 'var(--orange)', fontWeight: 700, fontSize: 15 },
  staffOrders:   { color: 'var(--text-muted)', fontSize: 11 },
  stockRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                   padding: '10px 14px', background: 'var(--surface)', borderRadius: 8,
                   marginBottom: 6, borderLeft: '3px solid var(--warning)' },
  stockName:     { color: 'var(--text)', fontWeight: 700, fontSize: 15 },
  stockCategory: { color: 'var(--text-muted)', fontSize: 11 },
  stockSearchInput: { display: 'block', width: '100%', padding: '8px 10px', margin: '0 0 10px',
                   background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6,
                   color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  restockInput:  { width: 64, padding: '6px 8px', background: 'var(--surface-2)', border: '1px solid var(--border)',
                   borderRadius: 6, color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' },
  restockBtn:    { padding: '6px 10px', background: 'var(--orange)', border: 'none', borderRadius: 6,
                   color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  empty:         { color: 'var(--text-dim)', textAlign: 'center', padding: '60px 20px', fontSize: 15, letterSpacing: 1 },
  periodReopenBtn: { padding: '8px 14px', background: 'var(--orange)', border: 'none', borderRadius: 6,
                   color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                   letterSpacing: 1, whiteSpace: 'nowrap' },
};

// ─── Menu-specific styles ──────────────────────────────────────────────────────

const m = {
  actionBar:   { display: 'flex', gap: 8, padding: '12px 12px 0' },
  addBtn:      { flex: 1, background: 'var(--orange)', border: 'none', color: '#fff',
                 padding: '10px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                 cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
                 letterSpacing: 1 },
  formCard:    { background: 'var(--surface)', margin: '10px 12px', borderRadius: 10,
                 padding: '14px', border: '1px solid var(--border)' },
  formTitle:   { color: 'var(--orange)', fontSize: 13, fontWeight: 700, letterSpacing: 2,
                 marginBottom: 10 },
  formError:   { color: 'var(--danger)', fontSize: 12, marginBottom: 8 },
  input:       { display: 'block', width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border)',
                 color: 'var(--text)', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                 fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 8,
                 boxSizing: 'border-box', outline: 'none' },
  select:      { display: 'block', width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border)',
                 color: 'var(--text)', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                 fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 8,
                 boxSizing: 'border-box', outline: 'none' },
  saveBtn:     { background: 'var(--orange)', border: 'none', color: '#fff', padding: '10px 20px',
                 borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                 fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1 },
  catRow:      { display: 'flex', gap: 6, padding: '10px 12px', overflowX: 'auto' },
  catPill:     { flexShrink: 0, background: 'var(--surface)', border: '1px solid var(--border)',
                 color: 'var(--text-muted)', padding: '6px 12px', borderRadius: 20, fontSize: 12,
                 fontWeight: 700, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
                 letterSpacing: 1, whiteSpace: 'nowrap' },
  catPillActive:{ background: '#3a1a00', borderColor: 'var(--orange)', color: 'var(--orange)' },
  itemRow:     { display: 'flex', alignItems: 'flex-start', background: 'var(--surface)',
                 borderRadius: 8, padding: '10px 12px', marginBottom: 6,
                 borderLeft: '3px solid var(--orange)', gap: 8 },
  itemName:    { color: 'var(--text)', fontWeight: 700, fontSize: 15 },
  itemMeta:    { display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' },
  catBadge:    { background: 'var(--surface-2)', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700,
                 padding: '2px 6px', borderRadius: 4, letterSpacing: 1 },
  itemRight:   { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6,
                 flexShrink: 0 },
  itemPrice:   { color: 'var(--orange)', fontWeight: 700, fontSize: 15 },
  itemActions: { display: 'flex', gap: 4 },
  actionBtn:   { background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)',
                 borderRadius: 5, padding: '3px 7px', fontSize: 10, fontWeight: 700,
                 cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1 },
  lockCard:    { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
                 padding: '16px', marginBottom: 10, textAlign: 'center' },
  lockIcon:    { fontSize: 24, marginBottom: 6 },
  lockTitle:   { color: 'var(--text-dim)', fontSize: 14, fontWeight: 700, letterSpacing: 1, marginBottom: 4 },
  lockDesc:    { color: 'var(--text-dim)', fontSize: 12, lineHeight: 1.4, marginBottom: 10 },
  lockPill:    { display: 'inline-block', background: 'var(--surface)', color: 'var(--orange)',
                 border: '1px solid var(--orange)', borderRadius: 20, padding: '6px 14px',
                 fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: 'pointer' },
  bizCard:     { background: 'var(--surface)', borderRadius: 8, padding: '12px 14px',
                 marginBottom: 8, border: '1px solid var(--border)' },
  bizCardTitle:{ color: 'var(--orange)', fontSize: 13, fontWeight: 700, letterSpacing: 1,
                 marginBottom: 4 },
  bizCardDesc: { color: 'var(--text-dim)', fontSize: 12, lineHeight: 1.4 },
  insightRow:  { background: 'var(--surface)', borderRadius: 6, padding: '8px 10px', marginBottom: 6 },
  insightName: { color: 'var(--text)', fontWeight: 700, fontSize: 13 },
  insightStat: { color: 'var(--text-muted)', fontSize: 11, marginTop: 1 },
  insightTip:  { color: 'var(--text-muted)', fontSize: 12, marginTop: 4, lineHeight: 1.4 },
};

// ─── Roles Tab ────────────────────────────────────────────────────────────────

const PERMISSION_GROUPS = [
  {
    group: 'DASHBOARD & REPORTS',
    items: [
      { key: 'can_open_dashboard', label: 'Access Owner Dashboard' },
      { key: 'can_view_reports',   label: 'View Reports' },
    ],
  },
  {
    group: 'MANAGEMENT',
    items: [
      { key: 'can_open_management',  label: 'Edit Menu & Inventory' },
      { key: 'can_manage_staff',     label: 'Manage Staff' },
      { key: 'can_start_workperiod', label: 'Start / End Work Period' },
    ],
  },
  {
    group: 'TICKETS',
    items: [
      { key: 'can_view_all_tickets', label: "View All Waiters' Tickets" },
      { key: 'can_close_any_ticket', label: 'Close Any Ticket' },
      { key: 'can_void_lines',       label: 'Void Lines (no manager PIN)' },
      { key: 'can_merge_tickets',    label: 'Merge Tickets' },
      { key: 'can_change_table',     label: 'Move Ticket to Another Table' },
      { key: 'can_apply_discount',   label: 'Apply Discounts' },
      { key: 'can_comp_ticket',      label: 'Complimentary Tickets' },
      { key: 'can_edit_price',       label: 'Edit Item Prices' },
    ],
  },
  {
    group: 'PAYMENTS',
    items: [
      { key: 'can_take_payment', label: 'Process Payments' },
    ],
  },
];

const ROLE_COLS = [
  { role: 'owner',   label: 'OWNER',   locked: true },
  { role: 'manager', label: 'MANAGER', locked: false },
  { role: 'cashier', label: 'CASHIER', locked: false },
  { role: 'waiter',  label: 'WAITER',  locked: false },
];

function RolesTab({ perms, onToggle, onLoad, error }) {
  useEffect(() => { onLoad(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div style={{ ...s.empty, color: 'var(--warning)' }}>
        🔒 {error}
      </div>
    );
  }

  if (Object.keys(perms).length === 0) {
    return <div style={s.empty}>Loading permissions...</div>;
  }

  return (
    <div style={{ padding: '0 12px 20px' }}>
      <div style={rt.header}>
        <div style={rt.labelCol} />
        {ROLE_COLS.map(col => (
          <div key={col.role} style={rt.roleHeader}>
            <div style={{ color: col.locked ? 'var(--text-dim)' : 'var(--orange)', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
              {col.label}
            </div>
            {col.locked && <div style={{ color: 'var(--text-dim)', fontSize: 9, letterSpacing: 1 }}>LOCKED</div>}
          </div>
        ))}
      </div>

      {PERMISSION_GROUPS.map(group => (
        <div key={group.group} style={{ marginBottom: 4 }}>
          <div style={rt.groupTitle}>{group.group}</div>
          {group.items.map(item => (
            <div key={item.key} style={rt.row}>
              <div style={rt.labelCol}>
                <span style={rt.permLabel}>{item.label}</span>
              </div>
              {ROLE_COLS.map(col => {
                const enabled = col.locked ? true : (perms[col.role]?.[item.key] ?? false);
                return (
                  <div key={col.role} style={rt.toggleCol}>
                    <button
                      disabled={col.locked}
                      style={{
                        ...rt.toggle,
                        backgroundColor: enabled ? (col.locked ? '#3a5a3a' : 'var(--table-free)') : 'var(--surface-2)',
                        borderColor:     enabled ? (col.locked ? '#3a5a3a' : 'var(--success)') : 'var(--border)',
                        opacity:         col.locked ? 0.5 : 1,
                        cursor:          col.locked ? 'default' : 'pointer',
                      }}
                      onClick={() => !col.locked && onToggle(col.role, item.key, enabled)}
                    >
                      <span style={{ color: enabled ? 'var(--success)' : 'var(--text-dim)', fontSize: 11, fontWeight: 700 }}>
                        {enabled ? 'ON' : 'OFF'}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}

      <div style={{ color: 'var(--text-dim)', fontSize: 11, marginTop: 16, textAlign: 'center', letterSpacing: 1 }}>
        CHANGES TAKE EFFECT IMMEDIATELY · OWNER PERMISSIONS CANNOT BE CHANGED
      </div>
    </div>
  );
}

const rt = {
  header:     { display: 'flex', alignItems: 'center', padding: '12px 0 8px',
                borderBottom: '1px solid var(--surface)', marginBottom: 8 },
  labelCol:   { flex: 1 },
  roleHeader: { width: 64, textAlign: 'center' },
  groupTitle: { color: 'var(--orange)', fontSize: 11, fontWeight: 700, letterSpacing: 2,
                padding: '10px 0 6px', borderBottom: '1px solid var(--surface-2)', marginBottom: 4 },
  row:        { display: 'flex', alignItems: 'center', paddingBottom: 6 },
  permLabel:  { color: 'var(--text-muted)', fontSize: 13 },
  toggleCol:  { width: 64, display: 'flex', justifyContent: 'center' },
  toggle:     { border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px',
                background: 'var(--surface-2)', fontFamily: "'Barlow Condensed', sans-serif",
                minWidth: 44, textAlign: 'center' },
};

// ─── Periods Tab ────────────────────────────────────────────────────────────

function PeriodsTab({ periods, error }) {
  if (error) return <div style={{ ...s.empty, color: 'var(--warning)' }}>⚠ {error}</div>;
  if (periods.length === 0) return <div style={s.empty}>No work periods recorded yet</div>;

  return (
    <div style={s.section}>
      {periods.map(p => (
        <div key={p.id} style={pt.row}>
          <div style={pt.dates}>
            <div style={pt.dateLine}>
              {new Date(p.opened_at).toLocaleString()}
              {' → '}
              {p.closed_at
                ? new Date(p.closed_at).toLocaleString()
                : <span style={{ color: 'var(--success)', fontWeight: 700 }}>Open</span>}
            </div>
            <div style={pt.staffLine}>
              Opened by {p.opened_by_name || 'Unknown'}
              {p.closed_by_name && ` · Closed by ${p.closed_by_name}`}
            </div>
          </div>
          <div style={pt.stats}>
            <div style={pt.revenue}>KES {Number(p.revenue).toLocaleString()}</div>
            <div style={pt.ticketCount}>{p.ticket_count} ticket{p.ticket_count === 1 ? '' : 's'}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const pt = {
  row:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                 padding: '10px 14px', background: 'var(--surface)', borderRadius: 8, marginBottom: 6 },
  dates:       { flex: 1 },
  dateLine:    { color: 'var(--text)', fontSize: 13, fontWeight: 600 },
  staffLine:   { color: 'var(--text-muted)', fontSize: 11, marginTop: 2 },
  stats:       { textAlign: 'right', marginLeft: 12 },
  revenue:     { color: 'var(--orange)', fontSize: 15, fontWeight: 700 },
  ticketCount: { color: 'var(--text-dim)', fontSize: 11 },
};
